/**
 * String utility functions for common text formatting operations
 */

/**
 * Capitalizes the first letter of a string and converts the rest to lowercase
 * @param str - The input string to capitalize
 * @returns The capitalized string, or empty string if input is null/undefined/empty
 */
export const capitalizeFirstLetter = (str: string | null | undefined): string => {
  if (!str || str.length === 0) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Capitalizes the first letter of each word in a string
 * @param str - The input string to capitalize
 * @returns The string with each word capitalized, or empty string if input is null/undefined/empty
 */
export const capitalizeWords = (str: string | null | undefined): string => {
  if (!str || str.length === 0) return '';
  return str
    .split(' ')
    .map(word => capitalizeFirstLetter(word))
    .join(' ');
};

/**
 * Extracts the first alphabetic character from an email address
 * Handles quoted email format: "Name" <email@domain.com>
 * @param email - The email string to extract character from
 * @returns The first alphabetic character in uppercase, or 'U' if none found
 */
export const getFirstAlphabeticChar = (email: string | null | undefined): string => {
  if (!email) return 'U'; // Default to 'U' for User
  
  // Handle quoted email format: "Name" <email@domain.com>
  // Extract the name part from within quotes
  const quotedNameMatch = email.match(/^"([^"]+)"/);
  if (quotedNameMatch) {
    const name = quotedNameMatch[1].trim();
    // Find first alphabetic character in the name
    for (let i = 0; i < name.length; i++) {
      const char = name[i];
      if (/[a-zA-Z]/.test(char)) {
        return char.toUpperCase();
      }
    }
  }
  
  // If no quoted name found, try to extract from email address
  // Remove quotes and trim
  const cleanEmail = email.replace(/"/g, '').trim();
  
  // Find first alphabetic character (a-z, A-Z)
  for (let i = 0; i < cleanEmail.length; i++) {
    const char = cleanEmail[i];
    if (/[a-zA-Z]/.test(char)) {
      return char.toUpperCase();
    }
  }
  
  // If no alphabetic character found, return 'U'
  return 'U';
};

/**
 * Capitalizes the first letter of a channel name with special handling for 'email' and email addresses
 * @param channelName - The channel name to capitalize
 * @returns The properly capitalized channel name
 */
export const capitalizeChannelName = (channelName: string | null | undefined): string => {
  if (!channelName || channelName.length === 0) return '';
  
  // Check if channel name is an email address
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
  const isEmailAddress = emailRegex.test(channelName);
  
  // Special case: if channel name is 'email', keep it lowercase
  if (channelName.toLowerCase() === 'email') {
    return 'email';
  }
  
  // If it's an email address, return as-is (no capitalization)
  if (isEmailAddress) {
    return channelName;
  }
  
  // For all other channel names, capitalize the first letter
  return channelName.charAt(0).toUpperCase() + channelName.slice(1).toLowerCase();
};

/**
 * Generates a consistent color based on an email address or name
 * Uses the extracted name from quoted emails for better consistency
 * @param email - The email string to generate color from
 * @returns A hex color code
 */
export const getAvatarColor = (email: string | null | undefined): string => {
  if (!email) return '#2563EB'; // Default blue-600 (better contrast with white text)
  
  // Try to extract name from quoted format first
  const quotedNameMatch = email.match(/^"([^"]+)"/);
  let textToHash = email;
  
  if (quotedNameMatch) {
    // Use the name part for color generation
    textToHash = quotedNameMatch[1].trim();
  } else {
    // Fallback to cleaned email
    textToHash = email.replace(/"/g, '').trim();
  }
  
  const colors = [
    // User suggested colors
    '#1E3A8A', // Dark Blue
    '#4F46E5', // Indigo
    '#6D28D9', // Purple
    '#0F766E', // Teal
    '#065F46', // Emerald
    '#991B1B', // Red
    '#C2410C', // Orange
    '#78350F', // Amber
    '#1F2937', // Gray
    '#334155', // Slate
    '#7E22CE', // Violet
    '#172554', // Navy
    '#365314', // Green
    // Additional vibrant colors for better contrast with white text
    '#DC2626', // Red-600
    '#EA580C', // Orange-600
    '#D97706', // Amber-600
    '#CA8A04', // Yellow-600
    '#16A34A', // Green-600
    '#059669', // Emerald-600
    '#0891B2', // Cyan-600
    '#0284C7', // Sky-600
    '#2563EB', // Blue-600
    '#7C3AED', // Violet-600
    '#9333EA', // Purple-600
    '#C026D3', // Fuchsia-600
    '#DB2777', // Pink-600
    '#BE185D', // Rose-600
  ];
  
  // Use text length and characters to generate consistent color
  let hash = 0;
  for (let i = 0; i < textToHash.length; i++) {
    hash = textToHash.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};